import { gsap } from 'gsap';
import React, { CSSProperties, FocusEvent, PropsWithChildren, forwardRef, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Icon, InputContainer, InputElement, InputWrapper, Tooltip, mergeRefs } from '@column/column-ui-kit';

import { copyToClipboard } from '~/util';

export interface HiddenInputStyleProps {
  isVisible: boolean;
  isEmpty: boolean;
}

export interface HiddenInputProps extends Partial<HiddenInputStyleProps> {
  onChange?: (value: string) => void;
  onVisibleChange?: (value: boolean) => void;
  onReveal?: () => Promise<boolean>;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  value?: string;
  placeholder?: string;
  style?: CSSProperties;
  autoComplete?: 'username' | 'password' | 'off';
  defaultIsVisible?: boolean;
  isDisabled?: boolean;
  readOnly?: boolean;
  showCopy?: boolean;
  visibilityLabel?: string;
}

const StyledHiddenInput = styled(InputWrapper)``;

const HiddenInputContainer = styled(InputContainer)`
  display: flex;
`;

const Fields = styled.div`
  --hidden-input-visible-position: 200%;
  --hidden-input-hidden-position: 50%;
  position: relative;
  flex-grow: 1;
  padding-left: 8px;
`;

const StyledInputlement = styled(InputElement)<HiddenInputStyleProps>`
  mask-position: var(--hidden-input-visible-position);
  mask-size: 200%;
  mask-image: linear-gradient(-90deg, transparent 0%, black 25%, black 75%, transparent 100%);
  mask-repeat: no-repeat;
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
`;

const HiddenInputElement = styled(StyledInputlement)<HiddenInputStyleProps>`
  position: absolute;
  top: 0;
  mask-position: var(--hidden-input-hidden-position);
  pointer-events: ${({ isVisible }) => (isVisible ? 'none' : 'auto')};
`;

const EditableInputElement = styled(InputElement)``;

const Toggle = styled.button<HiddenInputStyleProps>`
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 6px;
  margin: -8px -6px;
  border-radius: 6px;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    --icon-size: 16px;

    path {
      transition:
        fill 0.2s,
        stroke 0.2s;
    }
  }

  ${({ isEmpty }) =>
    isEmpty &&
    css`
      pointer-events: none;
      svg {
        --icon-color: ${({ theme }) => theme.secondary.blendToBackground(300)};
      }
    `}

  &:hover {
    background: ${({ theme }) => theme.secondary.blendToBackground(60)};

    svg {
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary.blendToBackground(350)};
    outline-offset: 1px;
  }
`;

const Eye = styled(Icon.AnimationEye)`
  --eye-line-background-color: var(--input-background, ${({ theme }) => theme.secondary.blendToBackground(75)});
`;

const Copy = styled(Toggle)<HiddenInputStyleProps>`
  margin-left: auto;
`;

export const HiddenInput = forwardRef<HTMLInputElement, PropsWithChildren<HiddenInputProps>>((props, ref) => {
  const [isVisibleState, setIsVisibleState] = useState<boolean>(props.defaultIsVisible ?? false);
  const [currentValue, setCurrentValue] = useState<string>(props.value ?? '');
  const [isTooltipShow, setIsTooltipShow] = useState<boolean>(false);
  const [showCopied, setShowCopied] = useState<boolean>(false);

  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editableInputRef = useRef<HTMLInputElement>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const hasVisibilityBeenToggledRef = useRef(false);

  const styleProps: HiddenInputStyleProps = {
    isVisible: isVisibleState,
    isEmpty: !currentValue,
  };
  const isReadOnly = props.readOnly ?? true;
  const isEditable = !isReadOnly;
  const showCopy = props.showCopy ?? true;
  const visibilityLabel = props.visibilityLabel ?? 'API key';
  const canToggleVisibility = !styleProps.isEmpty || !!props.placeholder?.trim();
  const canCopy = showCopy && !styleProps.isEmpty && !props.isDisabled;

  const handleChange = (value: string) => {
    setCurrentValue(value);
    props.onChange?.(value);
  };

  const handleInternalButtonMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (e.target instanceof Element && e.target.closest('button')) {
      return;
    }

    const activeInput = isEditable
      ? editableInputRef.current
      : isVisibleState
        ? inputRef.current
        : hiddenInputRef.current;
    activeInput?.focus();
  };

  const handleVisibleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const isVisible = !isVisibleState;
    if (isVisible && props.onReveal && !currentValue && !(await props.onReveal())) {
      return;
    }
    hasVisibilityBeenToggledRef.current = true;
    if (props.onVisibleChange) {
      props.onVisibleChange(isVisible);
    }
    gsap.killTweensOf(fieldsRef.current);
    if (isVisible) {
      gsap.to(fieldsRef.current, {
        '--hidden-input-hidden-position': '-100%',
        duration: 0.4,
      });
      gsap.to(fieldsRef.current, {
        '--hidden-input-visible-position': '50%',
        duration: 0.4,
        delay: 0.1,
      });
    } else {
      gsap.set(fieldsRef.current, {
        '--hidden-input-hidden-position': '200%',
      });
      gsap.to(fieldsRef.current, {
        '--hidden-input-visible-position': '-100%',
        duration: 0.4,
      });
      gsap.to(fieldsRef.current, {
        '--hidden-input-hidden-position': '50%',
        duration: 0.4,
        delay: 0.1,
        onComplete: (target: HTMLDivElement) => {
          gsap.set(target, {
            '--hidden-input-visible-position': '200%',
            '--hidden-input-hidden-position': '50%',
          });
        },
        onCompleteParams: [fieldsRef.current],
      });
    }
    setIsVisibleState(isVisible);
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsTooltipShow(true);
    setShowCopied(true);
    copyToClipboard(currentValue);
    setTimeout(() => {
      setIsTooltipShow(false);
      setTimeout(() => setShowCopied(false), 200);
    }, 550);
  };

  useEffect(() => {
    if (!props.defaultIsVisible) {
      return;
    }
    const fields = fieldsRef.current;
    gsap.to(fields, {
      '--hidden-input-hidden-position': '-100%',
      duration: 0.4,
    });
    gsap.to(fields, {
      '--hidden-input-visible-position': '50%',
      duration: 0.4,
      delay: 0.1,
    });
    return () => gsap.killTweensOf(fields);
  }, [props.defaultIsVisible]);

  useEffect(() => {
    setCurrentValue(props.value ?? '');
  }, [props.value]);

  useEffect(() => {
    if (!currentValue && (!props.defaultIsVisible || !hasVisibilityBeenToggledRef.current)) {
      setIsVisibleState(props.defaultIsVisible ?? false);
    }
  }, [currentValue, props.defaultIsVisible]);

  return (
    <StyledHiddenInput style={props.style} className={props.className} {...styleProps}>
      <HiddenInputContainer onClick={handleContainerClick} {...styleProps}>
        {canToggleVisibility && (
          <Toggle
            onMouseDown={handleInternalButtonMouseDown}
            onClick={(e: React.MouseEvent) => void handleVisibleClick(e)}
            type="button"
            aria-label={isVisibleState ? `Hide ${visibilityLabel}` : `Show ${visibilityLabel}`}
            {...styleProps}
            isEmpty={false}
          >
            <Eye line={!isVisibleState} />
          </Toggle>
        )}
        <Fields ref={fieldsRef}>
          {isEditable ? (
            <EditableInputElement
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
              onBlur={props.onBlur}
              onFocus={props.onFocus}
              ref={mergeRefs([editableInputRef, ref])}
              value={currentValue}
              type={isVisibleState ? 'text' : 'password'}
              autoComplete={props.autoComplete ?? 'off'}
              disabled={props.isDisabled}
              readOnly={isReadOnly}
              placeholder={props.placeholder}
            />
          ) : (
            <>
              <HiddenInputElement
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
                onBlur={props.onBlur}
                onFocus={props.onFocus}
                ref={mergeRefs([hiddenInputRef, ref])}
                value={currentValue}
                type="password"
                autoComplete={props.autoComplete ?? 'off'}
                disabled={props.isDisabled}
                readOnly={isReadOnly}
                tabIndex={isVisibleState ? -1 : undefined}
                placeholder={props.placeholder}
                {...styleProps}
              />
              <StyledInputlement
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
                onBlur={props.onBlur}
                onFocus={props.onFocus}
                ref={inputRef}
                value={currentValue}
                type="text"
                autoComplete={props.autoComplete ?? 'off'}
                disabled={props.isDisabled}
                readOnly={isReadOnly}
                tabIndex={isVisibleState ? undefined : -1}
                placeholder={props.placeholder}
                {...styleProps}
              />
            </>
          )}
        </Fields>
        {canCopy && (
          <Copy
            onMouseDown={handleInternalButtonMouseDown}
            onClick={handleCopyClick}
            type="button"
            aria-label={`Copy ${visibilityLabel}`}
            {...styleProps}
          >
            <Tooltip
              content={showCopied ? 'Copied!' : currentValue}
              maxWidth="280px"
              zIndex={100}
              isOpen={isTooltipShow}
            >
              <Icon.Copy />
            </Tooltip>
          </Copy>
        )}
      </HiddenInputContainer>
      {props.children}
    </StyledHiddenInput>
  );
});
